import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, X } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Check if it already fired before React mounted
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
      setShowInstallBtn(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa-prompt-ready', () => {
      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
        setShowInstallBtn(true);
      }
    });

    // Listen to service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // If there's already a waiting worker, show the update prompt immediately
        if (registration.waiting) {
          setUpdateAvailable(true);
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          }
        });
      });

      // Handle controllerchange to force reload after update
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    
    // We've used the prompt, and can't use it again, throw it away
    setDeferredPrompt(null);
    setShowInstallBtn(false);
  };

  const handleUpdateClick = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        if (registration.waiting) {
          // Send message to SW to skip waiting and activate new version
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
  };

  // Render nothing if no prompt or update is available
  if (!showInstallBtn && !updateAvailable) return null;

  return (
    <>
      {/* Installation Prompt (Standard bottom bar) */}
      {!updateAvailable && showInstallBtn && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'var(--card-bg, #1e1e2d)',
          border: '1px solid var(--accent-color, #7928ca)',
          padding: '12px 20px',
          borderRadius: '30px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          zIndex: 9999,
          color: 'white',
          width: 'max-content',
          maxWidth: '90vw'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} color="var(--accent-color, #7928ca)" />
            <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Install VJLINK App</span>
          </div>
          <button 
            onClick={handleInstallClick}
            style={{ 
              background: 'var(--accent-color, #7928ca)', 
              color: 'white', 
              border: 'none', 
              padding: '6px 12px', 
              borderRadius: '20px', 
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.8rem'
            }}
          >
            Install
          </button>
          <button 
            onClick={() => setShowInstallBtn(false)}
            style={{ 
              background: 'transparent', 
              color: 'var(--text-muted, #9ca3af)', 
              border: 'none', 
              padding: '4px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Update Prompt (Prominent Pop-up) */}
      {updateAvailable && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <style>
            {`
              @keyframes popIn {
                0% { transform: scale(0.8); opacity: 0; }
                100% { transform: scale(1); opacity: 1; }
              }
              @keyframes fadeIn {
                0% { opacity: 0; }
                100% { opacity: 1; }
              }
            `}
          </style>
          <div style={{
            backgroundColor: 'var(--card-bg, #1e1e2d)',
            padding: '30px',
            borderRadius: '24px',
            border: '1px solid var(--accent-color, #7928ca)',
            textAlign: 'center',
            maxWidth: '320px',
            width: '90%',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            animation: 'popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <div style={{ 
              width: '64px', 
              height: '64px', 
              backgroundColor: 'rgba(121, 40, 202, 0.1)', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <RefreshCw size={32} color="var(--accent-color, #7928ca)" className="animate-spin-slow" />
            </div>
            <h2 style={{ color: 'white', marginBottom: '10px', fontSize: '1.5rem' }}>Update Ready!</h2>
            <p style={{ color: 'var(--text-muted, #9ca3af)', marginBottom: '25px', lineHeight: '1.5' }}>
              A new version of VJLINK with the updated branding is ready for you.
            </p>
            <button 
              onClick={handleUpdateClick}
              style={{ 
                background: 'linear-gradient(to right, #7928ca, #ff0080)', 
                color: 'white', 
                border: 'none', 
                padding: '12px 30px', 
                borderRadius: '12px', 
                cursor: 'pointer',
                fontWeight: '900',
                fontSize: '1rem',
                width: '100%',
                boxShadow: '0 4px 15px rgba(121, 40, 202, 0.4)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              UPDATE NOW
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
